import { PersonalTimerHost } from "@/features/time-tracking/PersonalTimerHost";
import { useState, useCallback, useEffect, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  useConfirmUnsavedChanges,
  useGuardedNavClick,
} from "@/contexts/UnsavedChangesContext";
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Briefcase,
  Building2,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronsUp,
  CircleDollarSign,
  CircleUser,
  ClipboardList,
  CreditCard,
  FileText,
  Flag,
  GitBranch,
  Headphones,
  Inbox,
  LayoutDashboard,
  Mail,
  MessageCircle,
  Network,
  LogOut,
  Menu,
  Megaphone,
  MessagesSquare,
  MessageSquare,
  MessageSquareDashed,
  Newspaper,
  PhoneCall,
  Settings,
  Shield,
  Star,
  Search,
  Sparkles,
  StickyNote,
  UserCheck,
  Users,
  X,
  BookOpen,
} from "lucide-react";
import { SOCIAL_ENGAGEMENT_ENABLED } from "@/lib/social-flags";
import { useUnlinkedEngagementCount } from "@/features/social/api";
import { useInboxUnreadTotal } from "@/features/inbox/api";
import { AIInsightsSidebar, AIInsightsToggle, type Message as AIMessage } from "@/features/ai/AIInsightsSidebar";
import { ErrorBoundary } from "./ErrorBoundary";
import { RouteTransition } from "./brand/motion";
import { LogoutButton } from "./LogoutButton";
import { NewLeadButton } from "@/features/leads/NewLeadButton";
import { OrgSwitcher } from "./OrgSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useIsRelayPlatformAdmin } from "@/hooks/useIsRelayPlatformAdmin";
import { useIsAgencyAdmin } from "@/features/agency/hooks";
import {
  useFinanceAccess,
} from "@/features/finance/access";
import { useFeatureAddon, useFeatureGate } from "@/features/plan/hooks";
import { useAgencySession } from "@/features/agency/AgencySessionContext";
import { GlobalSearchPalette, OPEN_SEARCH_EVENT } from "@/features/search/GlobalSearchPalette";
import { useEndClientSession } from "@/features/agency/api";
import { cn } from "@/lib/utils";
import { getUserDisplayName } from "@/lib/user-display";
import { NotificationBell } from "@/features/notifications";
import { useCurrentBook } from "@/features/ledger/api";
import { useOrgCapabilities } from "@/features/settings/useOrgCapabilities";

/* ------------------------------------------------------------------ */
/*  Nav definition                                                     */
/* ------------------------------------------------------------------ */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true, renders a small red presence dot on the nav label. */
  dotBadge?: boolean;
  /**
   * Extra path prefixes that also mark this item active. Lets one rail entry
   * (e.g. "CRM" → /contacts) stay highlighted across sibling surfaces
   * (/companies, /deals) reached through its in-page tabs.
   */
  activeMatch?: string[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const SIDEBAR_EXPANDED_KEY = "relay-sidebar-expanded";

/* ------------------------------------------------------------------ */
/*  Brand mark                                                         */
/* ------------------------------------------------------------------ */

/**
 * Relay logo mark — the Inspired Edge upward-chevron motif in a coral
 * gradient tile. Used in the sidebar header and the mobile top bar.
 */
function BrandMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-coral shadow-glow",
        className,
      )}
    >
      <ChevronsUp className="h-5 w-5 text-white" strokeWidth={2.75} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar nav item                                                   */
/* ------------------------------------------------------------------ */

function SidebarItem({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const Icon = item.icon;
  const location = useLocation();
  const guardedClick = useGuardedNavClick();
  // Sibling surfaces reached through a hub item's in-page tabs (e.g. /companies,
  // /deals under "CRM") keep the rail entry highlighted.
  const matchedBySibling =
    item.activeMatch?.some((prefix) => location.pathname.startsWith(prefix)) ?? false;
  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/settings"}
      onClick={guardedClick}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
          isActive || matchedBySibling
            ? "bg-primary/10 font-semibold text-primary shadow-[0_4px_16px_-8px_hsl(var(--primary)/0.55)] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-primary"
            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
          collapsed && "justify-center px-2",
        )
      }
    >
      <Icon
        className={cn(
          "shrink-0 transition-transform duration-200 group-hover:scale-110",
          collapsed ? "h-5 w-5" : "h-4 w-4",
        )}
      />
      {!collapsed && (
        <span className="relative truncate">
          {item.label}
          {item.dotBadge && (
            <span className="absolute -top-1 -right-3 h-2 w-2 rounded-full bg-red-500" />
          )}
        </span>
      )}
      {/* Show dot on icon when collapsed */}
      {collapsed && item.dotBadge && (
        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={12}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

/* ------------------------------------------------------------------ */
/*  Sidebar nav group                                                  */
/* ------------------------------------------------------------------ */

function SidebarGroup({
  group,
  collapsed,
  dotBadgeRoutes = {},
  defaultOpen = true,
}: {
  group: NavGroup;
  collapsed: boolean;
  /** Map of route `to` → true/false; when true the item renders a dot badge. */
  dotBadgeRoutes?: Record<string, boolean>;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const location = useLocation();

  // Auto-open group if current route matches one of its items (or an item's
  // activeMatch sibling prefix — e.g. CRM on /companies, /deals).
  useEffect(() => {
    const isActive = group.items.some(
      (item) =>
        location.pathname.startsWith(item.to) ||
        (item.activeMatch?.some((prefix) => location.pathname.startsWith(prefix)) ?? false),
    );
    if (isActive && !open) setOpen(true);
  }, [location.pathname, group.items, open]);

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 py-1">
        {group.items.map((item) => (
          <SidebarItem
            key={item.to}
            item={{ ...item, dotBadge: dotBadgeRoutes[item.to] === true }}
            collapsed
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-muted-foreground"
      >
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform duration-200",
            !open && "-rotate-90",
          )}
        />
        {group.label}
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="flex flex-col gap-0.5 pl-1">
          {group.items.map((item) => (
            <SidebarItem
              key={item.to}
              item={{ ...item, dotBadge: dotBadgeRoutes[item.to] === true }}
              collapsed={false}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile overlay                                                     */
/* ------------------------------------------------------------------ */

function MobileOverlay({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  // Close on Escape. Registered on the capture phase and calling
  // preventDefault() — same convention Radix's DismissableLayer primitives
  // use — so this always runs, and marks the event handled, before any
  // bubble-phase Escape listener elsewhere on the page (e.g. the social
  // composer's) can react to the same keydown. Capture guarantees the
  // ordering regardless of which component's effect happened to register
  // its listener first.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handler, { capture: true });
    return () => document.removeEventListener("keydown", handler, { capture: true });
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-background/80 backdrop-blur-sm transition-opacity duration-300 md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
        aria-hidden
      />
      {/* Drawer */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-72 transform bg-sidebar shadow-2xl transition-transform duration-300 ease-out md:hidden",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {children}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  User footer                                                        */
/* ------------------------------------------------------------------ */

function UserFooter({ collapsed }: { collapsed: boolean }) {
  const { user } = useAuth();
  const { organization, organizationRole } = useOrganization();
  const guardedClick = useGuardedNavClick();
  const confirmNavigate = useConfirmUnsavedChanges();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-accent/50",
            collapsed && "justify-center",
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CircleUser className="h-4 w-4" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {getUserDisplayName(user)}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {organization?.name ?? user?.email ?? ""}
              </p>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align="start"
        className="w-56"
      >
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {getUserDisplayName(user)}
            </span>
            {user?.email && (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
            )}
            {organization && (
              <span className="text-xs font-normal text-muted-foreground">
                {organization.name}
                {organizationRole ? ` · ${organizationRole}` : ""}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings?tab=profile" className="flex items-center" onClick={guardedClick}>
            <CircleUser className="mr-2 h-4 w-4" /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center" onClick={guardedClick}>
            <Settings className="mr-2 h-4 w-4" /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <LogoutButton asMenuItem confirmNavigate={confirmNavigate}>
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </LogoutButton>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar content (shared between desktop & mobile)                  */
/* ------------------------------------------------------------------ */

function SidebarContent({
  collapsed,
  onToggle,
  onMobileClose,
  onInsightsToggle,
  confirmNavigate = () => true,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
  onInsightsToggle: () => void;
  confirmNavigate?: () => boolean;
}) {
  const location = useLocation();
  const { branding } = useBranding();
  const { isPlatformAdmin } = useIsRelayPlatformAdmin();
  const { isAgencyAdmin } = useIsAgencyAdmin();
  // Finance nav item — same role gate as the /finance route and the AR/AP
  // cards (FINANCE_DASHBOARD_VISIBILITY); mirrors the Agency group pattern.
  const { allowed: financeAllowed } = useFinanceAccess();
  // Books (#176), for orgs with the opt-in "ledger" addon. Before a book exists,
  // owners/admins see it so they can set one up; afterwards it follows the
  // server's ledger_can(read) answer from ledger_book_for_org (spec §2.8). The
  // client role is presentation only (agency sessions invent "admin").
  const { enabled: ledgerEnabled } = useFeatureAddon("ledger");
  const { isAdmin: orgAdmin } = useOrgCapabilities();
  const { assignment: ledgerAssignment } = useCurrentBook(ledgerEnabled);
  const booksVisible = ledgerEnabled && (ledgerAssignment ? ledgerAssignment.can_read : orgAdmin);

  // Feature gates — gates only DISABLE: absent = enabled; explicit false = hidden.
  const { enabled: socialEnabled } = useFeatureGate("social");
  const { enabled: emailCampaignsEnabled } = useFeatureGate("email_campaigns");
  const { enabled: smsCampaignsEnabled } = useFeatureGate("sms_campaigns");
  const { enabled: sequencesEnabled } = useFeatureGate("sequences");
  const { enabled: formsEnabled } = useFeatureGate("forms");
  const { enabled: bookingsEnabled } = useFeatureGate("bookings");
  const { enabled: agencyGateEnabled } = useFeatureGate("agency");

  // Unlinked engagement dot badge — only fires when flag is on.
  const { data: unlinkedCount = 0 } = useUnlinkedEngagementCount();

  // Inbox unread total — drives the dot badge on the Inbox nav item.
  const { organizationId } = useOrganization();
  const { data: inboxUnreadTotal = 0 } = useInboxUnreadTotal(organizationId);

  const dotBadgeRoutes: Record<string, boolean> = {
    "/social/engagements": SOCIAL_ENGAGEMENT_ENABLED && unlinkedCount > 0,
    "/inbox": inboxUnreadTotal > 0,
  };

  // Agency nav group — only visible to agency org owners/admins AND when agency gate is enabled.
  const agencyNavGroup: NavGroup = {
    label: "Agency",
    items: [
      { to: "/agency", label: "Overview", icon: Network },
      { to: "/agency/clients", label: "Clients", icon: Users },
      { to: "/agency/plans", label: "Plans", icon: CreditCard },
    ],
  };

  // Build gated nav groups — filter items hidden by feature gates.
  // Each gated item is identified by its `to` path prefix.
  const gatedOutreach: NavItem[] = [
    { to: "/inbox", label: "Inbox", icon: MessagesSquare },
    { to: "/sms", label: "SMS", icon: MessageSquare },
    { to: "/sms-campaigns", label: "SMS Campaigns", icon: MessageSquareDashed },
    { to: "/calls", label: "Calls", icon: PhoneCall },
    { to: "/emails", label: "Emails", icon: Mail },
    { to: "/email-templates", label: "Email Templates", icon: FileText },
    { to: "/articles", label: "Articles", icon: Newspaper },
    ...(sequencesEnabled ? [{ to: "/sequences", label: "Sequences", icon: GitBranch }] : []),
    ...(formsEnabled ? [{ to: "/forms", label: "Forms", icon: ClipboardList }] : []),
    ...(socialEnabled ? [
      { to: "/social", label: "Social", icon: Megaphone },
      { to: "/social/engagements", label: "Engagements", icon: MessageCircle },
      { to: "/reviews", label: "Reviews", icon: Star },
    ] : []),
  ];

  // Email Campaigns lives in a separate context (campaigns page); gate it independently.
  // SMS Campaigns mirrors that pattern with its own gate (sms_campaigns).
  // If email_campaigns is disabled, remove /emails + /email-templates + /articles
  // (articles are only useful when linked from a campaign) from outreach.
  // If sms_campaigns is disabled, remove /sms-campaigns from outreach.
  const filteredOutreach = gatedOutreach
    .filter((item) =>
      emailCampaignsEnabled
        ? true
        : item.to !== "/emails" &&
          item.to !== "/email-templates" &&
          item.to !== "/articles",
    )
    .filter((item) => (smsCampaignsEnabled ? true : item.to !== "/sms-campaigns"));

  const gatedOperations: NavItem[] = [
    ...(bookingsEnabled
      ? [
          { to: "/bookings", label: "Bookings", icon: Calendar },
          { to: "/referrals", label: "Referrals", icon: Users },
        ]
      : []),
    { to: "/projects", label: "Projects", icon: Briefcase },
    { to: "/ugc", label: "UGC", icon: Sparkles },
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/tasks", label: "Tasks", icon: CheckSquare },
    { to: "/notes", label: "Notes", icon: StickyNote },
    ...(financeAllowed
      ? [{ to: "/finance", label: "Finance", icon: CircleDollarSign }]
      : []),
    ...(booksVisible ? [{ to: "/books", label: "Books", icon: BookOpen }] : []),
    { to: "/reporting", label: "Reporting", icon: BarChart2 },
  ];

  // Individual settings destinations (Automations, CI, Custom Fields,
  // Webhooks, Client Portal, Branding, Reviews) now live in the consolidated
  // Settings rail (SettingsLayout), so the sidebar Admin group only exposes
  // the Settings entry point.
  const gatedAdmin: NavItem[] = [
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  const gatedNavGroups: NavGroup[] = [
    {
      label: "CRM",
      items: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/follow-ups", label: "Follow-ups", icon: Inbox },
        // Contacts / Segments / Companies / Deals collapse into one hub entry
        // with in-page tabs (CrmHubTabs); the rail stays shallow on laptops.
        // /contacts is the landing; activeMatch keeps CRM lit on the siblings.
        {
          to: "/contacts",
          label: "CRM",
          icon: Users,
          activeMatch: ["/contacts", "/companies", "/deals"],
        },
      ],
    },
    {
      label: "Outreach",
      items: filteredOutreach,
    },
    {
      label: "Operations",
      items: gatedOperations,
    },
    {
      label: "Admin",
      items: gatedAdmin,
    },
  ];

  // Platform-admin nav group is conditional — only operators with the
  // relay_platform_admin / super_admin JWT role see it. Mirror of Forge
  // app.forgehq.io/admin pattern; each product hosts its own platform
  // admin under its own domain.
  const navGroups: NavGroup[] = [
    ...gatedNavGroups,
    ...(isAgencyAdmin && agencyGateEnabled ? [agencyNavGroup] : []),
    ...(isPlatformAdmin
      ? [
          {
            label: "Platform",
            items: [
              {
                to: "/admin/platform",
                label: "Overview",
                icon: LayoutDashboard,
              },
              {
                to: "/admin/platform/organizations",
                label: "Organizations",
                icon: Building2,
              },
              {
                to: "/admin/platform/users",
                label: "Users",
                icon: Users,
              },
              {
                to: "/admin/platform/audit",
                label: "Audit logs",
                icon: Shield,
              },
              {
                to: "/admin/platform/observability",
                label: "Function logs",
                icon: Activity,
              },
              {
                // Jobs = background-queue observability (public.jobs) —
                // operator tooling, so it lives with the other platform
                // tools. The route carries the same PlatformAdminRoute gate.
                to: "/admin/platform/jobs",
                label: "Jobs",
                icon: Activity,
              },
              {
                to: "/admin/platform/billing",
                label: "Billing",
                icon: CreditCard,
              },
              {
                to: "/admin/platform/support-sessions",
                label: "Support sessions",
                icon: Headphones,
              },
              {
                to: "/admin/platform/impersonation",
                label: "Impersonation",
                icon: UserCheck,
              },
              {
                to: "/admin/platform/feature-flags",
                label: "Feature flags",
                icon: Flag,
              },
              {
                to: "/admin/platform/ci",
                label: "CI",
                icon: GitBranch,
              },
            ],
          } satisfies NavGroup,
        ]
      : []),
  ];

  // Auto-close mobile drawer on route change
  useEffect(() => {
    onMobileClose?.();
  }, [location.pathname, onMobileClose]);

  return (
    <div className="flex h-full flex-col">
      {/* Brand header */}
      <div
        className={cn(
          "flex h-14 shrink-0 items-center border-b px-4",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <BrandMark />
            <span className="flex flex-col leading-tight">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.appName} className="h-7 object-contain max-w-[140px]" />
              ) : (
                <span className="font-display text-lg leading-none">{branding.appName}</span>
              )}
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                Inspired Edge
              </span>
            </span>
          </Link>
        )}
        {collapsed && (
          <Link to="/dashboard" aria-label={branding.appName + " — dashboard"}>
            <BrandMark />
          </Link>
        )}
        {/* Collapse toggle — desktop only */}
        <button
          type="button"
          onClick={onToggle}
          className="hidden rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:inline-flex"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              collapsed && "rotate-180",
            )}
          />
        </button>
        {/* Close button — mobile only */}
        {onMobileClose && (
          <button
            type="button"
            onClick={onMobileClose}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Org switcher — only visible when user belongs to multiple orgs */}
      <div className={cn("shrink-0 border-b px-3 py-2", collapsed && "px-2")}>
        <OrgSwitcher collapsed={collapsed} confirmNavigate={confirmNavigate} />
      </div>

      {/* Global search trigger — opens the Cmd/Ctrl+K palette */}
      <div className={cn("shrink-0 px-3 pt-3", collapsed && "px-2")}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))}
          className={cn(
            "flex w-full items-center gap-2 rounded-md border border-input bg-background text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            collapsed ? "justify-center p-2" : "px-3 py-2",
          )}
          aria-label="Search"
          title="Search (⌘K)"
        >
          <Search className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Search…</span>
              <kbd className="pointer-events-none rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
                ⌘K
              </kbd>
            </>
          )}
        </button>
      </div>

      {/* Primary create action — person-vs-company lead chooser */}
      <div className={cn("shrink-0 px-3 pt-3", collapsed && "px-2")}>
        <NewLeadButton collapsed={collapsed} confirmNavigate={confirmNavigate} />
      </div>

      {/* Navigation groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="flex flex-col gap-4">
          {navGroups.map((group, i) => (
            <div key={group.label}>
              {i > 0 && !collapsed && <Separator className="mb-3" />}
              {i > 0 && collapsed && (
                <div className="mx-auto mb-2 mt-1 h-px w-6 bg-border" />
              )}
              <SidebarGroup
                group={group}
                collapsed={collapsed}
                dotBadgeRoutes={dotBadgeRoutes}
              />
            </div>
          ))}
        </div>
      </nav>

      {/* Notifications + theme switch + AI Insights + user / org footer */}
      <div className="shrink-0 space-y-2 border-t border-sidebar-border p-3">
        <NotificationBell collapsed={collapsed} confirmNavigate={confirmNavigate} />
        <ThemeToggle collapsed={collapsed} />
        <AIInsightsToggle onClick={onInsightsToggle} collapsed={collapsed} />
        <UserFooter collapsed={collapsed} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AppShell (main export)                                             */
/* ------------------------------------------------------------------ */

/**
 * Shared layout for authenticated pages. Collapsible sidebar with grouped
 * navigation, responsive mobile drawer, and org/user footer.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { branding } = useBranding();
  const location = useLocation();
  const confirmUnsavedNavigation = useConfirmUnsavedChanges();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_KEY) === "false";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // AI Insights sidebar state — lifted here so toggle button (in sidebar) and
  // panel (rendered at AppShell level) share the same open/close state.
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightsMessages, setInsightsMessages] = useState<AIMessage[]>([]);
  const [insightsQuestion, setInsightsQuestion] = useState("");

  const { sessionId, clientOrgName, endSession: deactivate } = useAgencySession();
  const endClientSession = useEndClientSession();

  const handleExitBanner = useCallback(async () => {
    // Ending a support session changes the active org context even though it
    // doesn't look like route navigation — guard it the same as a nav action
    // so a dirty dashboard draft isn't silently abandoned.
    if (!confirmUnsavedNavigation()) return;
    if (sessionId) {
      try { await endClientSession.mutateAsync(sessionId); } catch { /* swallow */ }
    }
    deactivate();
  }, [confirmUnsavedNavigation, sessionId, endClientSession, deactivate]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(!next));
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleInsights = useCallback(() => setInsightsOpen((v) => !v), []);
  const toggleMobileInsights = useCallback(() => {
    setMobileOpen(false);
    setInsightsOpen((v) => !v);
  }, []);
  const closeInsights = useCallback(() => setInsightsOpen(false), []);

  return (
    <TooltipProvider delayDuration={300} skipDelayDuration={200}>
      {/* App-wide Cmd/Ctrl+K search palette */}
      <GlobalSearchPalette confirmNavigate={confirmUnsavedNavigation} />
      <div className="flex h-screen overflow-hidden bg-background">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden shrink-0 border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out md:flex md:flex-col",
            collapsed ? "w-[68px]" : "w-64",
          )}
        >
          <SidebarContent
            collapsed={collapsed}
            onToggle={toggleCollapsed}
            onInsightsToggle={toggleInsights}
            confirmNavigate={confirmUnsavedNavigation}
          />
        </aside>

        {/* Mobile drawer */}
        <MobileOverlay open={mobileOpen} onClose={closeMobile}>
          <SidebarContent
            collapsed={false}
            onToggle={toggleCollapsed}
            onMobileClose={closeMobile}
            onInsightsToggle={toggleMobileInsights}
            confirmNavigate={confirmUnsavedNavigation}
          />
        </MobileOverlay>

        {/* Main area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Agency session banner */}
          {clientOrgName && (
            <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-sm font-medium text-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Operating as <strong className="ml-1">{clientOrgName}</strong>
              </div>
              <button
                onClick={handleExitBanner}
                className="rounded px-3 py-1 text-xs font-semibold bg-white/20 hover:bg-white/30 transition"
              >
                Exit session
              </button>
            </div>
          )}

          {/* Mobile header */}
          <header className="flex h-14 shrink-0 items-center border-b bg-background px-4 md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/dashboard" className="ml-3 flex items-center gap-2.5">
              <BrandMark />
              <span className="flex flex-col leading-tight">
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt={branding.appName} className="h-7 object-contain max-w-[140px]" />
                ) : (
                  <span className="font-display text-lg leading-none">{branding.appName}</span>
                )}
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  Inspired Edge
                </span>
              </span>
            </Link>
          </header>

          {/* Page content. Wrapped in its own ErrorBoundary so a crash in a
              page (e.g. a single dashboard HUD card) renders a contained error
              INSIDE the shell instead of unwinding to the app-root boundary in
              main.tsx, which would blank the whole app — sidebar, header, and
              hamburger menu included. Keyed by pathname so navigating away (via
              the still-present menu) resets the boundary and recovers.
              RouteTransition reuses that same pathname key to fade each new
              route's content in. */}
          <main className="flex-1 overflow-y-auto">
            <ErrorBoundary key={location.pathname}>
              <RouteTransition routeKey={location.pathname} className="h-full">
                {children}
              </RouteTransition>
            </ErrorBoundary>
          </main>
        </div>

        <PersonalTimerHost />

        {/* AI Insights collapsible sidebar — fixed right-side panel */}
        <AIInsightsSidebar
          open={insightsOpen}
          onClose={closeInsights}
          messages={insightsMessages}
          onMessages={setInsightsMessages}
          question={insightsQuestion}
          onQuestion={setInsightsQuestion}
        />
      </div>
    </TooltipProvider>
  );
}
